import { useLocation } from 'react-router';
import * as React from 'react';
import { BottomNavigation, BottomNavigationAction } from '@mui/material';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useEffect } from 'react';
import {
	atoSmartDocsPageUrl,
	communityPageUrl,
	dashboardPageUrl,
	fvaScorePageUrl,
	loginPageUrl,
	notificationsCentrePageUrl,
	policiesPageUrl,
	userSocialProfileEditPageUrl
} from '../../../../core/constants';
import NavAlertIcon from '../../../common/svgIcons/NavAlertIcon';
import NavHomeIcon from '../../../common/svgIcons/NavHomeIcon';
import NavCommunityIcon from '../../../common/svgIcons/NavCommunityIcon';
import {
	bottomNavBarHeight,
	bottomNavBarV2Height,
	errorRed,
	screenBottomNavZIndex,
	tabletPortraitMinWidth,
	whiteColour
} from '../../../cssConstants';
import NavMaccasIcon from '../../../common/svgIcons/NavMaccasIcon';
import useClientContextStore from '../../../../zustandStore/clientContextStore';
import { getCommunityFeedName, isBetterOffApp, isMcProgressApp } from '../../../../core/domain';
import NavAtoDocIcon from '../../../common/svgIcons/NavAtoDocIcon';
import BottomModal from '../../../../commonComponents/bottomModal/BottomModal';
import LockIcon from '../../../common/svgIcons/LockIcon';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import { useFeedToken, useFeedUserProfile } from '../../../../core/api/feeds';
import useStreamFeedStore from '../../../../zustandStore/streamFeedStore';
import useChatStore from '../../../../zustandStore/chatStore';
import useNotificationStore from '../../../../zustandStore/notificationStore';
import NavPoliciesIcon from '../../../common/svgIcons/NavPoliciesIcon';
import { isAndroidApp } from '../../../../lib/utils';

const isBetterApp = isBetterOffApp();
const isMcProgress = isMcProgressApp();

const BottomNavBarV2 = () => {
	const navigate = useNavigateWithAppName();
	const location = useLocation();
	const { pathname } = location;
	const [navActionItemValue, setNavActionItemValue] = React.useState(getInitialNavItemValue(pathname));

	// All Nav items state, Home and Vault items are always there, so no need to set
	const [showFinishProfileSetupModal, setShowFinishProfileSetupModal] = React.useState(false);
	const userKey = useClientContextStore((state) => state.clientDetails?.userKey);
	const feedClient = useStreamFeedStore((state) => state.feedClient);
	const userProfileOverview = useFeedUserProfile(feedClient, userKey);
	const setFeedClientAndActivityFeed = useStreamFeedStore((state) => state.setFeedClientAndActivityFeed);
	const feedToken = useFeedToken();

	// Chat and notification unread status
	const unreadChatCount = useChatStore((state) => state.unreadChatCount);
	const unreadNotificationCount = useNotificationStore((state) => state.unreadNotificationCount);
	const shouldShowUnreadIndicator = unreadChatCount > 0 || unreadNotificationCount > 0;

	useEffect(() => {
		const appFeedName = getCommunityFeedName();
		// Initialize feed client here globally
		if (userKey && feedToken && !feedClient) {
			setFeedClientAndActivityFeed(userKey, feedToken, appFeedName);
		}
	}, [userKey, feedToken, feedClient, setFeedClientAndActivityFeed]);

	const handleCommunityClick = () => {
		if (userProfileOverview !== undefined) {
			// Check if profile is complete
			if (!userProfileOverview?.hasUserFilledProfile) {
				setShowFinishProfileSetupModal(true);
			} else {
				navigate(communityPageUrl);
			}
		}
	};

	// Handle modal close
	const handleModalClose = () => {
		setShowFinishProfileSetupModal(false);
	};

	// Handle profile edit navigation
	const handleProfileEdit = () => {
		navigate(userSocialProfileEditPageUrl);
		setShowFinishProfileSetupModal(false);
	};

	useEffect(() => {
		const initialNavItemValue = getInitialNavItemValue(pathname);
		setNavActionItemValue(initialNavItemValue);
	}, [pathname]);

	return (
		<>
			<StyledNav
				showLabels
				value={navActionItemValue}
				onChange={(event: React.SyntheticEvent, newValue: string) => {
					setNavActionItemValue(newValue);
				}}
			>
				{/* Keep the following duplicated code because some Material UI feature won't work after moving it into a styled component. */}
				<BottomNavigationAction
					component={Link}
					to={dashboardPageUrl}
					value={dashboardPageUrl}
					label="Home"
					icon={<NavHomeIcon isSelected={navActionItemValue === dashboardPageUrl} />}
				/>
				{isMcProgress && (
					<BottomNavigationAction
						component={Link}
						to={fvaScorePageUrl}
						value={fvaScorePageUrl}
						label="FVA Score"
						icon={<NavMaccasIcon isSelected={[fvaScorePageUrl].includes(navActionItemValue)} />}
					/>
				)}
				{!isMcProgress && !isBetterApp && (
					<BottomNavigationAction
						component={Link}
						to={atoSmartDocsPageUrl}
						value={atoSmartDocsPageUrl}
						label="ATO Docs"
						icon={<NavAtoDocIcon isSelected={[atoSmartDocsPageUrl].includes(navActionItemValue)} />}
					/>
				)}
				<BottomNavigationAction
					onClick={handleCommunityClick}
					value={communityPageUrl}
					label="Community"
					icon={<NavCommunityIcon isSelected={navActionItemValue === communityPageUrl} />}
				/>
				{isBetterApp && (
					<BottomNavigationAction
						component={Link}
						to={policiesPageUrl}
						value={policiesPageUrl}
						label="Policies"
						icon={<NavPoliciesIcon isSelected={navActionItemValue === policiesPageUrl} />}
					/>
				)}
				{/* Remove for time being as per request, can add back when needed */}
				{/*  : (
					<BottomNavigationAction
						component={Link}
						to={documentVaultPageUrl}
						value={documentVaultPageUrl}
						label="Vault"
						icon={
							<NavVaultIcon
								isSelected={[
									documentVaultPageUrl,
									documentUploadPageUrl,
									documentViewPageUrl,
									documentVaultResetPinPageUrl
								].includes(navActionItemValue)}
							/>
						}
					/>
				) */}
				<BottomNavigationAction
					component={Link}
					to={notificationsCentrePageUrl}
					value={notificationsCentrePageUrl}
					label="Messages"
					icon={
						<>
							{shouldShowUnreadIndicator && <UnreadStatusIndicator />}
							<NavAlertIcon isSelected={navActionItemValue === notificationsCentrePageUrl} />
						</>
					}
				/>
			</StyledNav>

			{/* Profile completion modal */}
			<BottomModal
				open={showFinishProfileSetupModal}
				onClose={handleModalClose}
				imageComponent={<LockIcon />}
				title={isBetterApp ? 'Setup Social Profile' : 'Finish setting up your community profile'}
				body={['Update your profile to unlock access to the Marketplace.']}
				actions={[
					{
						label: 'Finish profile setup',
						callback: handleProfileEdit
					}
				]}
			/>
		</>
	);
};

export default BottomNavBarV2;

const getInitialNavItemValue = (pathname: string) => {
	if ([loginPageUrl, dashboardPageUrl].includes(pathname)) return dashboardPageUrl;
	return pathname;
};

const selectedNavItemCss = css`
	&::before {
		content: '';
		position: absolute;
		top: 12px;
		bottom: 28px;
		width: 86px;
		background: rgba(255, 255, 255, 0.85);
		border-radius: 12px;
		z-index: -1;
	}
`;

const StyledNav = styled(BottomNavigation)`
	position: fixed;
	display: flex;
	overflow: hidden;
	bottom: 0;
	width: 100%;
	padding: 0 25px;
	background-color: ${(props) => props.theme.brandingColourHeavy};
	height: ${isAndroidApp ? `calc(${bottomNavBarHeight} + var(--safe-area-inset-bottom))` : bottomNavBarV2Height};
	z-index: ${screenBottomNavZIndex};
	touch-action: none;
	overscroll-behavior: contain;

	@media (min-width: ${tabletPortraitMinWidth}) {
		padding: 0 calc((100% - 600px) / 2);
	}

	> .MuiBottomNavigationAction-root {
		position: relative;
		height: ${bottomNavBarV2Height};
		width: 25%;
		max-width: 25%;
		min-width: 90px; // min screen 360px / 4 = 90px
		color: ${whiteColour};
		padding: 16px 0 36px 0;
		justify-content: space-around;

		&.Mui-selected {
			color: ${(props) => props.theme.brandingColourHeavy};
			text-align: center;
			font-weight: 500;

			${selectedNavItemCss}
		}

		.MuiBottomNavigationAction-label {
			font-size: 12px;
			line-height: 12px;
			margin-top: 10px;
		}
	}
`;

const UnreadStatusIndicator = styled.div`
	position: absolute;
	top: 17px;
	left: 53%;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background: ${errorRed};
	border: 2px solid white;
`;
